import clsx from 'clsx';
import AddRuleDropdown from '../AddRuleDropdown';
import QuestionRule from '../QuestionRule';
import { QuestionTextPreview } from './QuestionTextPreview';
import { useScrollNewItemIntoView } from 'components/Rules/helpers/useScrollNewItemIntoView';
import RulesToolbar from 'components/Rules/RulesToolbar';
import {
  ECreateQuestionMode,
  useQuestionEditorContext,
} from 'components/Questionsv2/builder/context/create-question.context';
import { removeQuestionRule } from 'rtk/Question.Editor.Slice';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestion,
  selectQuestionIsReadonly,
  selectQuestionMode,
  selectQuestionRules,
} from 'rtk/questionEditorSelector';

const QuestionRules = () => {
  const { questionId } = useQuestionEditorContext();
  const dispatch = useAppDispatch();
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const rules = useAppSelector((state) => selectQuestionRules(state, questionId));
  const question = useAppSelector((state) => selectQuestion(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  // Matches EditorContent's own grid split (2:1, preview only in Edit mode) so the rules content
  // lines up with the editor's content column instead of being independently centered.
  const showPreviewWidth = mode === ECreateQuestionMode.Edit;
  const listRef = useScrollNewItemIntoView<HTMLDivElement>(rules.length);

  // question.texts is the per-language cache; the currently-active editing language may not be in
  // it yet, so fall back to question.text (already resolved for that one language) for that case.
  const textByLanguage = { ...question?.texts };
  if (question?.language && !textByLanguage[question.language] && question.text) {
    textByLanguage[question.language] = question.text;
  }

  return (
    <div className={clsx('grid h-full w-full gap-4', showPreviewWidth ? 'grid-cols-3' : 'grid-cols-1')}>
      <div className={clsx(showPreviewWidth ? 'col-span-2' : 'col-span-1', 'flex h-full min-h-0 flex-col gap-2')}>
        <div className="bg-primary-50 p-2 px-4">
          <div className="flex flex-col justify-between">
            <div className="pb-4">
              <QuestionTextPreview
                availableLanguages={question?.availableLanguages ?? []}
                textByLanguage={Object.fromEntries(
                  Object.entries(textByLanguage).map(([language, data]) => [language, data?.value ?? []]),
                )}
              />
            </div>

            <RulesToolbar
              isReadOnly={isReadOnly}
              hasRules={rules.length > 0}
              addRuleDropdown={<AddRuleDropdown />}
              onDeleteAll={() => rules.forEach((rule) => dispatch(removeQuestionRule({ questionId, ruleId: rule.id })))}
            />
          </div>
        </div>
        <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
          {rules.map((rule, ruleIdx) => (
            <QuestionRule key={rule.id} rule={rule} ruleIdx={ruleIdx} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuestionRules;
